import { SiteLayout } from "@/components/site/SiteLayout";
import { HeroBackground } from "@/components/site/HeroBackground";
import { stockImages } from "@/data/site/stockImages";
import { PageSEO } from "@/components/site/PageSEO";
import { brand } from "@/data/site/brand";

interface LegalPageProps {
  slug: "privacy-policy" | "terms" | "disclaimer";
}

const content = {
  "privacy-policy": {
    title: "Privacy Policy",
    description: `How ${brand.legalName} collects, uses, and protects your information.`,
    body: (
      <>
        <p>
          {brand.legalName} ("we", "us") respects your privacy. This policy explains what we
          collect on this website and how we use it.
        </p>
        <h2>Information we collect</h2>
        <p>
          When you contact us through the website (forms, phone, email), we collect the contact and
          financial details you provide so we can evaluate your tax matter and respond. We also
          collect standard server logs and analytics (pages visited, device, referrer) to operate
          and improve the site.
        </p>
        <h2>How we use it</h2>
        <ul>
          <li>To respond to your inquiry and provide tax-resolution services.</li>
          <li>To send service-related communications.</li>
          <li>To comply with legal and professional obligations.</li>
        </ul>
        <h2>We do not sell your information</h2>
        <p>
          We do not sell personal information. We share information with service providers only as
          needed (e.g., secure hosting, email delivery, IRS filings) and they are bound to use it
          only for those purposes.
        </p>
        <h2>Your rights</h2>
        <p>
          You may request access to, correction of, or deletion of your information by emailing{" "}
          <a href={`mailto:${brand.email}`}>{brand.email}</a>.
        </p>
        <h2>Contact</h2>
        <p>
          {brand.legalName} · {brand.address.full} · {brand.phone} ·{" "}
          <a href={`mailto:${brand.email}`}>{brand.email}</a>
        </p>
      </>
    ),
  },
  terms: {
    title: "Terms of Use",
    description: `Terms governing your use of the ${brand.legalName} website.`,
    body: (
      <>
        <p>
          By using this website you agree to these terms. The site is provided for informational
          purposes about {brand.legalName}'s services and does not create a CPA-client relationship
          until a written engagement letter is signed.
        </p>
        <h2>No professional advice</h2>
        <p>
          Content on this site is general information about IRS procedures and tax resolution. It
          is not tax, accounting, or legal advice for your specific situation. Always consult a
          credentialed professional before acting.
        </p>
        <h2>Intellectual property</h2>
        <p>
          All site content (text, graphics, logos) is owned by {brand.legalName} or its licensors
          and may not be reproduced without permission.
        </p>
        <h2>Limitation of liability</h2>
        <p>
          The site is provided "as is" without warranties. {brand.legalName} is not liable for any
          loss arising from use of the site.
        </p>
        <h2>Governing law</h2>
        <p>These terms are governed by the laws of the State of Texas.</p>
      </>
    ),
  },
  disclaimer: {
    title: "Disclaimer",
    description: `Important disclaimers about ${brand.legalName} services and outcomes.`,
    body: (
      <>
        <h2>Not a law firm</h2>
        <p>
          {brand.legalName} is a CPA firm. We are not a law firm and we do not provide legal
          advice. CPAs are federally authorized tax practitioners and may represent taxpayers
          before the IRS in all matters of tax administration.
        </p>
        <h2>Results vary</h2>
        <p>
          Every tax case is unique. Outcomes depend on facts including total liability, income,
          assets, age of debt, statute of limitations, and IRS program eligibility. Past results
          described on this site do not guarantee a similar outcome.
        </p>
        <h2>No CPA-client relationship</h2>
        <p>
          Submitting a form or calling does not by itself create a CPA-client relationship. That
          relationship is established only by a written engagement letter signed by both parties.
        </p>
        <h2>Testimonials</h2>
        <p>
          Client testimonials reflect individual experience and are not paid. They are not a
          guarantee of similar results.
        </p>
      </>
    ),
  },
} as const;

const LegalPage = ({ slug }: LegalPageProps) => {
  const c = content[slug];
  return (
    <SiteLayout>
      <PageSEO title={c.title} description={c.description} path={`/${slug}`} noindex image={stockImages.accountingDesk.src} />
      <section className="relative bg-gradient-navy text-white overflow-hidden">
        <HeroBackground image={stockImages.accountingDesk} priority />
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12 lg:py-28 max-w-6xl">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mb-4">Legal</p>
          <h1 className="font-heading text-4xl lg:text-6xl font-bold leading-[1.05] mb-3 [text-wrap:balance]">
            {c.title}
          </h1>
          <p className="text-sm text-white/60">Last updated: June 2026</p>
        </div>
      </section>
      <section className="py-16 lg:py-32 bg-white">
        <div className="container mx-auto px-4 max-w-3xl prose prose-slate prose-headings:font-heading prose-headings:text-navy prose-a:text-navy prose-h2:text-2xl prose-h2:mt-10 prose-p:leading-[1.75] prose-p:text-[17px]">
          {c.body}
        </div>
      </section>
    </SiteLayout>
  );
};

export default LegalPage;
